import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PreferencesContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

export const PreferencesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [preferences, setPreferences] = useState(() => {
    // Load from localStorage immediately - this is synchronous and available on first render
    const stored = localStorage.getItem('exam_preferences');
    return stored ? JSON.parse(stored) : {
      exam_type: null,
      grade: null,
      mmd_location: null
    };
  });

  // Sync preferences with backend when user logs in (background operation)
  useEffect(() => {
    if (isAuthenticated()) {
      syncPreferences();
    }
  }, [isAuthenticated()]);

  const syncPreferences = async () => {
    try {
      // Fetch from backend
      const response = await axios.get(`${API}/preferences`);
      const backendPrefs = response.data;
      
      // Only update if backend has valid preferences
      if (backendPrefs && (backendPrefs.grade || backendPrefs.mmd_location)) {
        setPreferences(backendPrefs);
        localStorage.setItem('exam_preferences', JSON.stringify(backendPrefs));
      }
    } catch (error) {
      // If 404, backend doesn't have preferences - keep localStorage data
      if (error.response?.status !== 404) {
        console.error('Failed to sync preferences:', error);
      }
    }
  };

  const updatePreferences = async (newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    
    // Save to localStorage for guest users
    localStorage.setItem('exam_preferences', JSON.stringify(updated));
    
    // Save to backend if authenticated
    if (isAuthenticated()) {
      try {
        // Try PUT first (update existing), fall back to POST (create new)
        try {
          await axios.put(`${API}/preferences`, newPrefs);
        } catch (putError) {
          if (putError.response?.status === 404) {
            // Preferences don't exist, create them
            await axios.post(`${API}/preferences`, newPrefs);
          } else {
            throw putError;
          }
        }
      } catch (error) {
        console.error('Failed to update preferences:', error);
      }
    }
  };

  const value = {
    preferences,
    updatePreferences,
    syncPreferences
  };

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};